import { PropsWithChildren, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'

import { useEulaContext } from '../contexts/EulaContext'
import { Column, Row } from './pure/FlexContainer'
import FullScreenLoading from './FullScreenLoading'
import t from '../../tools/t'

// const A4_PX = { width: 1240, height: 1754 }
// const A3_PX = { width: A4_PX.width / 2, height: A4_PX.height / 2 }
const EULA_SIZE = { width: 1200, height: 850 }
const FULL_SIZE = { width: '100%', height: '100%' }

interface EulaViewerProps {
    onCancel?: () => void
}

export default function EulaViewer({ onCancel }: EulaViewerProps) {
    const [ready, setReady] = useState(false)
    const { eulaPdf, loading, error, eulaAccepted, acceptEula, rejectEula } = useEulaContext()

    useEffect(() => {
        if (loading || error) return
        // Due to how embeds initialize and render data,
        //  we must manually set the dimensions after the data has successfully been loaded,
        // The embed element doesn't do this, even if dimensions are set beforehand.
        if (eulaPdf) setReady(true)
    }, [eulaPdf, loading, error])

    return (
        <Column className="eula-viewer" style={EULA_SIZE} gap={5}>
            <embed
                className="eula-viewer__embed"
                style={ready ? FULL_SIZE : undefined}
                src={eulaPdf}
            />
            <Row className="eula-viewer__actions" arrangement="spaced">
                {eulaAccepted && onCancel && (
                    <Button
                        severity="contrast"
                        text
                        label={t('common_cancel')}
                        onClick={onCancel}
                    />
                )}
                <Button
                    severity="danger"
                    text
                    label={eulaAccepted ? t('eula_button_revoke') : t('eula_button_reject')}
                    onClick={rejectEula}
                />
                {!eulaAccepted && (
                    <Button
                        severity="secondary"
                        outlined
                        label={t('eula_button_accept')}
                        onClick={acceptEula}
                    />
                )}
            </Row>
        </Column>
    )
}

export const EulaDialog = () => {
    const { eulaOpen, hideEula } = useEulaContext()
    return (
        <Dialog visible={eulaOpen} onHide={hideEula} draggable={false}>
            <EulaViewer onCancel={hideEula} />
        </Dialog>
    )
}

interface EulaScreenProps extends PropsWithChildren {
    loading: boolean
    hasUser: boolean
    hasEula: boolean
    consent: boolean
}
export const EulaScreen = ({ loading, hasUser, hasEula, consent, children }: EulaScreenProps) => {
    if (loading) return <FullScreenLoading />
    if (hasEula && !consent)
        return (
            <div className="eula-screen">
                <Column className="eula-screen__header">
                    <div className="eula-screen__title">{t('eula_title')}</div>
                    <div className="eula-screen__description">{t('eula_description')}</div>
                </Column>
                <EulaViewer />
            </div>
        )

    if (hasUser && (!hasEula || consent)) return children
}
